import {
  Actions,
  ActionsButton,
  ActionsGroup,
  ActionsLabel,
  Block,
  BlockTitle,
  List,
  ListButton,
  ListInput,
  Navbar,
  Page,
  Preloader,
  f7,
} from 'framework7-react';
import { useEffect, useState } from 'react';
import LabelColorPicker from '@/features/labels/components/LabelColorPicker';
import {
  useCreateLabel,
  useDeleteLabel,
  useLabels,
  useUpdateLabel,
} from '@/features/labels/hooks/useLabels';
import LabelChips from '@/features/labels/components/LabelChips';
import SettingsSaveNav from '@/components/SettingsSaveNav';
import { apiErrorText } from '@/api/errors';
import { useErrorT, useT } from '@/i18n';
import { useSettingsDetailBackLink } from '@/utils/useMediaQuery';
import type { Label } from '@restaurant/api-contracts';

const toast = (text: string) => f7.toast.show({ text, closeTimeout: 2000, position: 'bottom' });

const DEFAULT_COLOR = '#0091ff';

type Draft = { name: string; color: string };

function deriveDraft(label: Label | undefined): Draft {
  return { name: label?.name ?? '', color: label?.color ?? DEFAULT_COLOR };
}

export default function LabelFormPage({ f7route }: { f7route: { params: { labelId?: string } } }) {
  const { labelId } = f7route.params;
  const editing = !!labelId;

  const ol = useT().labels;
  const errorT = useErrorT();
  const settingsBackLink = useSettingsDetailBackLink();
  const { data: labels } = useLabels();
  const label = editing ? labels?.find((l) => l.id === labelId) : undefined;

  const create = useCreateLabel();
  const update = useUpdateLabel();
  const del = useDeleteLabel();
  const [deleteSheetOpen, setDeleteSheetOpen] = useState(false);

  const [draft, setDraft] = useState<Draft | null>(() =>
    editing ? (label ? deriveDraft(label) : null) : deriveDraft(undefined),
  );

  useEffect(() => {
    if (draft || !editing) return;
    if (label) setDraft(deriveDraft(label));
  }, [draft, editing, label]);

  const back = () => f7.views.current.router.back();
  const patch = (p: Partial<Draft>) => setDraft((d) => (d ? { ...d, ...p } : d));

  const name = draft?.name.trim() ?? '';
  const baseline = deriveDraft(label);
  const isDirty = !!draft && (draft.name !== baseline.name || draft.color !== baseline.color);
  const saving = create.isPending || update.isPending;

  const submit = () => {
    if (!draft) return;
    if (!name) {
      toast(ol.nameRequired);
      return;
    }
    const body = { name, color: draft.color };
    const onError = (e: unknown) => toast(apiErrorText(e, errorT, ol.couldNotSave));

    if (editing && labelId) {
      update.mutate(
        { labelId, data: body },
        {
          onSuccess: () => {
            toast(ol.labelUpdated);
            back();
          },
          onError,
        },
      );
      return;
    }
    create.mutate(body, {
      onSuccess: () => {
        toast(ol.labelAdded);
        back();
      },
      onError,
    });
  };

  const confirmDelete = () => {
    if (!labelId) return;
    setDeleteSheetOpen(false);
    del.mutate(labelId, {
      onSuccess: () => {
        toast(ol.labelDeleted);
        back();
      },
      onError: (e) => toast(apiErrorText(e, errorT, ol.couldNotDelete)),
    });
  };

  return (
    <Page name="order-label-form">
      <Navbar title={editing ? ol.editLabel : ol.addLabel} backLink={settingsBackLink}>
        <SettingsSaveNav isDirty={isDirty} saving={saving} onSave={submit} />
      </Navbar>

      {!draft ? (
        <Block className="text-align-center">
          <Preloader />
        </Block>
      ) : (
        <>
          <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
            <ListInput
              type="text"
              label={ol.nameLabel}
              placeholder={ol.namePlaceholder}
              value={draft.name}
              maxlength={30}
              onChange={(e) => patch({ name: e.target.value })}
            />
          </List>

          <Block>
            <LabelColorPicker
              value={draft.color}
              onChange={(color) => patch({ color })}
              label={ol.colorLabel}
              customLabel={ol.customColor}
            />
          </Block>

          <BlockTitle>{ol.preview}</BlockTitle>
          <Block>
            <LabelChips
              options={[{ id: 'preview', name: name || ol.namePlaceholder, color: draft.color }]}
              selectedIds={['preview']}
            />
          </Block>

          {editing && label?.systemOwned && <Block>{ol.systemOwnedNote}</Block>}
          {editing && !label?.systemOwned && (
            <List strongIos strongMd insetIos insetMd>
              <ListButton color="red" onClick={() => setDeleteSheetOpen(true)}>
                {ol.deleteTitle}
              </ListButton>
            </List>
          )}
        </>
      )}

      <Actions opened={deleteSheetOpen} onActionsClosed={() => setDeleteSheetOpen(false)}>
        <ActionsGroup>
          <ActionsLabel>{ol.deleteConfirmation(label?.name ?? '')}</ActionsLabel>
          <ActionsButton color="red" onClick={confirmDelete}>
            {ol.deleteTitle}
          </ActionsButton>
        </ActionsGroup>
        <ActionsGroup>
          <ActionsButton strong>{ol.cancel}</ActionsButton>
        </ActionsGroup>
      </Actions>
    </Page>
  );
}
